// 📄 pages/Proyectos/components/FacturaDetallePanel.tsx
// Panel deslizable con el detalle completo de UNA factura (ver
// BuscarFacturas.tsx) — mismo criterio que ProveedorDetallePanel.tsx
// (SLP): secciones por tipo de dato + un botón "Ver JSON completo" para
// cualquier campo que Ariba devuelva y esta pantalla no modele
// explícitamente (direcciones incluidas: la documentación de Ariba no da
// los nombres de campo exactos de fromAddress/soldToAddress/etc. dentro
// del objeto, solo los describe en prosa — se renderizan de forma
// genérica, tomando cualquier valor de texto no vacío del objeto, en vez
// de adivinar claves y arriesgarse a no mostrar nada).
import { useState } from 'react';
import { X, MapPin, Calendar, DollarSign, Braces, Copy, Check } from 'lucide-react';
import { Factura } from '../../../services/comercioFacturasService.ts';

interface FacturaDetallePanelProps {
  factura: Factura;
  onClose: () => void;
}

const colorEstado = (valor?: string): 'ok' | 'warn' | 'bad' | 'neutral' => {
  const v = (valor || '').toLowerCase();
  if (['paid', 'approved', 'sent', 'acknowledged'].includes(v)) return 'ok';
  if (['pending approval', 'in progress', 'on hold', 'queued'].includes(v)) return 'warn';
  if (['canceled', 'failed', 'rejected', 'obsoleted'].includes(v)) return 'bad';
  return 'neutral';
};

const Badge = ({ children, tono = 'neutral' }: { children: React.ReactNode; tono?: 'neutral' | 'ok' | 'warn' | 'bad' }) => {
  const clases = {
    neutral: 'text-gray-400 bg-white/5 border-white/10',
    ok: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    warn: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    bad: 'text-red-300 bg-red-500/10 border-red-500/30'
  }[tono];
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${clases}`}>{children}</span>;
};

const Campo = ({ label, valor }: { label: string; valor?: string | number | null }) => {
  if (valor === undefined || valor === null || valor === '') return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-sm text-gray-200">{valor}</p>
    </div>
  );
};

const formatearFecha = (valor?: string): string | null => {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatearMonto = (monto?: { amount?: number; currencyCode?: string }): string | null => {
  if (!monto || typeof monto.amount !== 'number') return null;
  return `${monto.amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${monto.currencyCode || ''}`.trim();
};

// Renderiza una dirección sin asumir nombres de campo exactos (la doc de
// Ariba solo los describe en prosa) — junta cualquier valor de texto no
// vacío del objeto, en el orden en que Ariba lo devuelva.
const formatearDireccion = (direccion?: Record<string, any>): string | null => {
  if (!direccion || typeof direccion !== 'object') return null;
  const partes = Object.values(direccion).filter((v) => typeof v === 'string' && v.trim());
  return partes.length > 0 ? partes.join(', ') : null;
};

export default function FacturaDetallePanel({ factura, onClose }: FacturaDetallePanelProps) {
  const [mostrarJson, setMostrarJson] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const direcciones: { label: string; valor: string | null }[] = [
    { label: 'Desde (proveedor)', valor: formatearDireccion(factura.fromAddress) },
    { label: 'Comprado por', valor: formatearDireccion(factura.soldToAddress) },
    { label: 'Facturado a', valor: formatearDireccion(factura.billToAddress) },
    { label: 'Pago a', valor: formatearDireccion(factura.remitToAddress) }
  ].filter((d) => d.valor);

  const handleCopiarJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(factura, null, 2));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch (err) {
      console.error('Error al copiar el JSON:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg h-full bg-[rgb(var(--c-surface))] border-l border-white/10 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-5 border-b border-white/10 bg-[rgb(var(--c-surface))]">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{factura.documentNumber || 'Sin número'}</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{factura.supplierName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <div className="flex flex-wrap gap-1.5">
            {factura.documentStatus && <Badge tono={colorEstado(factura.documentStatus)}>{factura.documentStatus}</Badge>}
            {factura.routingStatus && <Badge>{factura.routingStatus}</Badge>}
            {factura.type && <Badge>{factura.type}</Badge>}
            {factura.purpose && <Badge>{factura.purpose}</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Campo label="Proveedor" valor={factura.supplierName} />
            <Campo label="Supplier ANID" valor={factura.supplierANID} />
            <Campo label="Comprador" valor={factura.buyerName} />
            <Campo label="Buyer ANID" valor={factura.buyerANID} />
            <Campo label="N° factura externo" valor={factura.externalInvoiceNumber} />
            <Campo label="N° orden externo" valor={factura.externalOrderNumber} />
            <Campo label="Ítems" valor={factura.numberOfItems} />
            <Campo label="Método de envío" valor={factura.submissionMethod} />
            <Campo label="Términos netos (días)" valor={factura.netTerms} />
            <Campo label="País origen" valor={factura.signFromCountry} />
            <Campo label="País destino" valor={factura.signToCountry} />
          </div>

          {(formatearMonto(factura.totalAmount) || formatearMonto(factura.tax)) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-indigo-400" /> Montos
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Total" valor={formatearMonto(factura.totalAmount)} />
                <Campo label="Impuesto" valor={formatearMonto(factura.tax)} />
              </div>
            </div>
          )}

          {direcciones.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-indigo-400" /> Direcciones
              </h4>
              <div className="space-y-2">
                {direcciones.map((d) => (
                  <div key={d.label}>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">{d.label}</p>
                    <p className="text-sm text-gray-300">{d.valor}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(formatearFecha(factura.createdDate) || formatearFecha(factura.documentDate) || formatearFecha(factura.documentStatusDate) || formatearFecha(factura.paymentDueDate)) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-400" /> Fechas
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Creada" valor={formatearFecha(factura.createdDate)} />
                <Campo label="Fecha documento" valor={formatearFecha(factura.documentDate)} />
                <Campo label="Último cambio de estado" valor={formatearFecha(factura.documentStatusDate)} />
                <Campo label="Vencimiento de pago" valor={formatearFecha(factura.paymentDueDate)} />
              </div>
            </div>
          )}

          {factura.documentStatusComments && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Comentarios del estado</h4>
              <p className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg p-3 whitespace-pre-wrap break-words">
                {factura.documentStatusComments}
              </p>
            </div>
          )}

          {(factura.buyerVatID || factura.supplierVatID || factura.buyerTaxID || factura.supplierTaxID) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Identificación fiscal</h4>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="VAT comprador" valor={factura.buyerVatID} />
                <Campo label="VAT proveedor" valor={factura.supplierVatID} />
                <Campo label="Tax ID comprador" valor={factura.buyerTaxID} />
                <Campo label="Tax ID proveedor" valor={factura.supplierTaxID} />
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={() => setMostrarJson((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
            >
              <Braces className="w-3.5 h-3.5" /> {mostrarJson ? 'Ocultar' : 'Ver'} JSON completo
            </button>
            {mostrarJson && (
              <div className="relative mt-2">
                <button
                  type="button"
                  onClick={handleCopiarJson}
                  title="Copiar JSON"
                  className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiado ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <pre className="bg-black/30 border border-white/10 rounded-lg p-3 text-[11px] text-gray-300 overflow-x-auto max-h-80 overflow-y-auto">
                  {JSON.stringify(factura, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
